import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface PageTitleContextValue {
  title: ReactNode;
  setTitle: (title: ReactNode) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<ReactNode>(null);
  const value = useMemo<PageTitleContextValue>(() => ({ title, setTitle }), [title]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

function usePageTitleContext(): PageTitleContextValue {
  const value = useContext(PageTitleContext);

  if (!value) {
    throw new Error("usePageTitleContext must be used within a PageTitleProvider.");
  }

  return value;
}

/** Reads page-owned title content. Used by the shared layout only. */
export function usePageTitleSlot(): ReactNode {
  return usePageTitleContext().title;
}

/** Replaces the route title while the calling page is mounted. */
export function usePageTitle(title: ReactNode): void {
  const { setTitle } = usePageTitleContext();

  useLayoutEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
}
